import React from 'react';
import { useLogosBancos } from '../../../../config/constants';
import { formatearFechaCompleta, formatearPrecio } from '../../../../utils/utils';
import { CreditCard, Mail, ShieldCheck, Globe, User, Phone, Fingerprint, Hash , X , Check } from 'lucide-react';
import { GeneralRoutes } from '../../../../utils/navigator';
import { useNavigate } from 'react-router-dom';

const HistorialPagosWidget = ({ pagos = [], handleDeletePago, estadosArchivos = {} , handleRechazarPago , handleAprobarPago}) => {
  const { getBancoLogo } = useLogosBancos();
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial de Pagos</h3>
      
      <div className="max-h-[700px] overflow-y-auto space-y-4 pr-2 custom-scrollbar">
        {pagos && pagos.length > 0 ? (
          pagos.map((pago) => {
            const pagoId = pago.id || pago.indexPago;
            const estadoArchivo = estadosArchivos[pagoId];
            const esPagoPendiente = pago.estado === "PENDIENTE";

            // Lógica de Identidad
            const esWompi = pago.metodopago?.toUpperCase() === 'WOMPI';
            const wompiData = pago.objetoWompi || {};

            return (
              <div 
                key={pagoId} 
                className={`rounded-xl border transition-all hover:shadow-md overflow-hidden ${
                  esWompi ? 'border-indigo-200 bg-white' : 'border-gray-200 bg-gray-50'
                }`}
              >
                {/* 1. ENCABEZADO: LOGO Y PRECIO */}
                <div className={`p-4 flex items-center justify-between ${esWompi ? 'bg-indigo-50/30' : 'bg-white/50'}`}>
                  <div className="flex items-center gap-4">
                    {/* LOGO DINÁMICO DESDE TU HOOK */}
                    <div className="h-12 w-12 flex items-center justify-center bg-white rounded-lg border border-gray-100 p-1 shadow-sm overflow-hidden">
                      <img 
                        src={getBancoLogo(pago.metodopago)} 
                        alt={pago.metodopago}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    
                    <div>
                      <p className={`font-black text-xl ${esWompi ? 'text-indigo-900' : 'text-blue-800'}`}>
                        {formatearPrecio(pago.valor)}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                          pago.estado === 'APROBADO' ? 'bg-green-100 text-green-700' : 
                          pago.estado === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {pago.estado}
                        </span>
                        {esWompi && (
                          <span className="flex items-center gap-1 text-[10px] text-indigo-600 font-bold uppercase tracking-tighter">
                            <ShieldCheck size={12} /> Pasarela Wompi
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleDeletePago && handleDeletePago(pago.indexPago)}
                    className="p-2 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <div className="p-4 space-y-4">

                  {/* 2. INFORMACIÓN EXCLUSIVA DE WOMPI */}
                    {esWompi && Object.keys(wompiData).length > 0 && (
                    <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-100 border-dashed shadow-inner">
                        {/* Encabezado de sección */}
                        <p className="text-[10px] font-black text-indigo-500 uppercase mb-3 flex items-center gap-1.5 tracking-widest">
                        <ShieldCheck size={12} className="text-indigo-600" /> 
                        Detalles de Transacción Verificada
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                        
                        {/* GRUPO: DATOS DEL CLIENTE */}
                        <div className="space-y-2">
                            <p className="text-[9px] font-bold text-indigo-300 uppercase">Información del Cliente</p>
                            
                            <div className="flex items-center gap-2 text-[11px]">
                            <div className="bg-indigo-100 p-1 rounded">
                                <User size={12} className="text-indigo-600" />
                            </div>
                            <span className="text-gray-500">Nombre:</span>
                            <span className="text-indigo-950 font-bold truncate">
                                {wompiData.customer_data?.full_name || 'No registrado'}
                            </span>
                            </div>

                            <div className="flex items-center gap-2 text-[11px]">
                            <div className="bg-indigo-100 p-1 rounded">
                                <Mail size={12} className="text-indigo-600" />
                            </div>
                            <span className="text-gray-500">Email:</span>
                            <span className="text-indigo-900 font-medium truncate italic">
                                {wompiData.customer_email || 'No registrado'}
                            </span>
                            </div>

                            <div className="flex items-center gap-2 text-[11px]">
                            <div className="bg-indigo-100 p-1 rounded">
                                <Phone size={12} className="text-indigo-600" />
                            </div>
                            <span className="text-gray-500">Teléfono:</span>
                            <span className="text-indigo-900 font-bold">
                                {wompiData.customer_data?.phone_number || 'No registrado'}
                            </span>
                            </div>
                        </div>

                        {/* GRUPO: IDENTIFICACIÓN Y MÉTODO */}
                        <div className="space-y-2">
                            <p className="text-[9px] font-bold text-indigo-300 uppercase">Documentación y Medio</p>
                            
                            <div className="flex items-center gap-2 text-[11px]">
                            <div className="bg-indigo-100 p-1 rounded">
                                <Fingerprint size={12} className="text-indigo-600" />
                            </div>
                            <span className="text-gray-500">{wompiData.billing_data?.legal_id_type || 'ID'}:</span>
                            <span className="text-indigo-900 font-bold">
                                {wompiData.billing_data?.legal_id || 'No registrado'}
                            </span>
                            </div>

                            <div className="flex items-center gap-2 text-[11px]">
                            <div className="bg-indigo-100 p-1 rounded">
                                <CreditCard size={12} className="text-indigo-600" />
                            </div>
                            <span className="text-gray-500">Medio de Pago:</span>
                            <span className="text-indigo-900 font-bold uppercase">
                                {wompiData.payment_method_type || 'N/A'}
                            </span>
                            </div>

                            {/* Badge de Red Usada (Si es Card) */}
                            {wompiData.payment_method?.extra?.brand && (
                            <div className="flex items-center gap-2 text-[11px]">
                                <div className="bg-indigo-100 p-1 rounded">
                                <Globe size={12} className="text-indigo-600" />
                                </div>
                                <span className="text-gray-500">Franquicia:</span>
                                <span className="text-indigo-900 font-bold uppercase">
                                {wompiData.payment_method.extra.brand} ****{wompiData.payment_method.extra.last_four}
                                </span>
                            </div>
                            )}
                        </div>

                        {/* ID DE TRANSACCIÓN ÚNICO (FOOTER DEL RECUADRO) */}
                        <div className="col-span-full mt-2 pt-2 border-t border-indigo-100/50 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                            <Hash size={10} className="text-indigo-300" />
                            <span className="text-[9px] text-indigo-400 font-mono">WOMPI_ID: {pago.id}</span>
                            </div>
                            <div className="text-[9px] text-indigo-400 italic">
                            Referencia: {wompiData.reference}
                            </div>
                        </div>

                        </div>
                    </div>
                    )}

                  {/* 3. DETALLES ESTÁNDAR (REFERENCIAS Y FECHAS) */}
                  <div className="grid grid-cols-2 gap-3 text-[11px]">
                    <div className="bg-white p-2 rounded border border-gray-100">
                      <span className="block text-gray-400 font-bold uppercase text-[9px]">Tipo de Registro</span>
                      <span className="text-gray-700 font-semibold">{pago.tipopago}</span>
                    </div>
                    <div className="bg-white p-2 rounded border border-gray-100">
                      <span className="block text-gray-400 font-bold uppercase text-[9px]">Referencia Pago</span>
                      <span className="text-gray-700 font-mono truncate block">{pago.referencia}</span>
                    </div>
                    <div className="col-span-1">
                      <span className="text-gray-400 font-medium mr-1">Fecha Pago:</span>
                      <span className="text-gray-600">{formatearFechaCompleta(pago.fechapago)}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className="text-gray-400 font-medium mr-1">Registro:</span>
                      <span className="text-gray-600">{formatearFechaCompleta(pago.fecharegistro)}</span>
                    </div>
                  </div>

                  {/* 4. SECCIÓN DE SOPORTES Y ARCHIVOS */}
                  <div className="pt-3 border-t border-gray-100">
                    {esWompi ? (
                      <button
                        onClick={() => GeneralRoutes.irAVerTransaccion(navigate,pago.referencia)}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all shadow-sm bg-[#3f309b] text-white hover:bg-[#2b1b81]"
                      >
                        <CreditCard size={14} />
                        Ver Estado de Transacción
                      </button>
                    ) : pago.linkPago ? (
                      <a
                        href={pago.linkPago}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all shadow-sm ${
                          esWompi ? 'bg-[#3f309b] text-white hover:bg-[#2b1b81]' : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {esWompi ? <CreditCard size={14} /> : '✅'} 
                        {esWompi ? 'Ver Comprobante Wompi' : 'Ver Comprobante Oficial'}
                      </a>
                    ) : (
                      pago.indexPago ? (
                        <div className="text-center">
                          <p className="text-[10px] text-gray-400 mb-1">Respaldo Digital (Drive)</p>
                          {!estadoArchivo && <p className="text-gray-400 animate-pulse text-[11px]">Buscando archivo...</p>}
                          {estadoArchivo?.status === 'found' && (
                            <a href={estadoArchivo.data.webViewLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-bold text-xs flex items-center justify-center gap-1">
                              📂 Soporte Encontrado
                            </a>
                          )}
                          {estadoArchivo?.status === 'not_found' && (
                            <p className="text-red-500 font-bold text-[10px] uppercase">Archivo no disponible en el servidor</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-[10px] text-center text-gray-400 italic">No se adjuntó soporte digital para este registro</p>
                      )
                    )}
                  </div>
                </div>

                {esPagoPendiente && (
                  <div className="px-4 pb-2">
                    <div className="flex gap-2">
                      {/* BOTÓN RECHAZAR - ROJO */}
                      <button
                        onClick={() => handleRechazarPago && handleRechazarPago(pago.indexPago,"DECLINADO")}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all border-2 border-red-100 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm"
                      >
                        <X size={14} strokeWidth={3} />
                        Rechazar
                      </button>

                      {/* BOTÓN VERIFICAR - VERDE */}
                      <button
                        onClick={() => handleAprobarPago && handleAprobarPago(pago.indexPago,"APROBADO")}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all border-2 border-green-100 bg-green-50 text-green-600 hover:bg-green-600 hover:text-white hover:border-green-600 shadow-sm"
                      >
                        <Check size={14} strokeWidth={3} />
                        Verificar
                      </button>
                    </div>
                    
                    {/* Separador visual sutil si hay contenido abajo */}
                    <div className="mt-4 border-t border-gray-100" />
                  </div>
                )}

              </div>
            );
          })
        ) : (
          <div className="text-center py-20 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <p className="text-gray-400 font-medium italic">Historial de pagos vacío</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorialPagosWidget;